/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/


import styled from "@emotion/styled";
import { TextField } from "@mui/material";
import { convertKey, saltKey } from "./env-url";
const CryptoJS = require("crypto-js/core");
CryptoJS.AES = require("crypto-js/aes");
const PBKDF2: any = require("crypto-js/pbkdf2");

export const ROLES = {
  DOC_VERIFICATION_USER: 'DOC_VERIFICATION_USER',
  DOC_ACQUISITION_USER: 'DOC_ACQUISITION_USER',
  DOC_SEARCH_USER: 'DOC_SEARCH_USER',
  DOC_SEARCH_DEMO_USER: 'DOC_SEARCH_DEMO_USER',
  DOC_VERIFICATION_ADMIN: 'DOC_VERIFICATION_ADMIN',
  SALES_REPRESENTATIVE: 'SALES_REPRESENTATIVE',
  USER_MGMT_ADMIN: 'USER_MGMT_ADMIN',
  GUEST: 'GUEST',
  SUPER_ADMIN: 'SUPER_ADMIN',
  FINANCIAL_MODEL_SUBSCRIBER: 'FINANCIAL_MODEL_SUBSCRIBER',
  CONTRACTS_TRAIL: 'CONTRACTS_TRAIL',
  FINANCIAL_MODELS_TRAIL: 'FINANCIAL_MODELS_TRAIL',
  CONTRACTS_SUBSCRIBER: 'CONTRACTS_SUBSCRIBER',
  PAYG_TEAM_ADMIN: 'PAYG_TEAM_ADMIN',
  PAYG_USER: 'PAYG_USER',
  SSO_USER: 'SSO_CONTRACTS_SUBSCRIBER',
  BASIC_ACCESS_SUBSCRIBER: 'BASIC_ACCESS_SUBSCRIBER',
  PRO_ACCESS_SUBSCRIBER: 'PRO_ACCESS_SUBSCRIBER',
  DATA_BROWSER_USER: "DATA_BROWSER_USER",
  BULKDATA_API_USER: "BULKDATA_API_USER",
  DATA_BROWSER_SUPER_USER: "DATA_BROWSER_SUPER_USER",
};

export const twilioAuthAndriod =
  "https://play.google.com/store/apps/details?id=com.authy.authy";
export const twilioAuthIos = "https://apps.apple.com/us/app/authy/id494168017";

export const microsoftAuthIos =
  "https://apps.apple.com/us/app/microsoft-authenticator/id983156458";
export const microsoftAuthAndriod =
  "https://play.google.com/store/apps/details?id=com.azure.authenticator";

export const googleAuthIos =
  "https://apps.apple.com/us/app/google-authenticator/id388497605";
export const googleAuthAndriod =
  "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2";


export const CssTextField = styled(TextField)({

  '& label': {
    color: "#D5D8DC",
  },
  "& label.Mui-focused": {
    color: "#D5D8DC",
  },
  "& label.Mui-error": {
    color: "#d32f2f",
  },

  "& .MuiSvgIcon-root": {
    color: "#D5D8DC",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#D5D8DC",
  },
  "& .MuiOutlinedInput-root": {
    color: "#D5D8DC",
    colorScheme: "dark",
    "& fieldset": {
      borderColor: "#D5D8DC",
    },
    "&:hover fieldset": {
      borderColor: "#D5D8DC",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#D5D8DC",
    },
    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
    },
    "& .MuiTypography-root": {
      color: "#D5D8DC",
    },
  },

  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#80808087 !important",
  },

  "& .MuiInputBase-input.Mui-disabled": {
    color: "#80808087 !important",
    "-webkit-text-fill-color": "#80808087 !important",
    borderColor: "#80808087 !important",
  },
  "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#80808087 !important",
  }
});
export const PermissionKey: any = {
  AUTH_ACTION_COLUMN_VIEW: 'AUTH_ACTION_COLUMN_VIEW',
  AUTH_UPLOAD_CSV_VIEW: 'AUTH_UPLOAD_CSV_VIEW',
  AUTH_USERS_TAB_VIEW: 'AUTH_USERS_TAB_VIEW',
  AUTH_STATS_TAB_VIEW: 'AUTH_STATS_TAB_VIEW',
  AUTH_USERS_TAB_EDIT: 'AUTH_USERS_TAB_EDIT',
  AUTH_CONFIG_TAB_VIEW: 'AUTH_CONFIG_TAB_VIEW'
}


export const encryptpassword = async (plaintext: any) => {
  const encryptedPassword_value = await encryptMyPassword(plaintext);
  return encryptedPassword_value;
}

export const encryptMyPassword = async (plaintext: string) => {
  const keySize = 128;
  const iterations = 1000;
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(16));

  const derivedKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(convertKey),
    { name: "PBKDF2" },
    false,
    ["deriveBits", "deriveKey"]
  );
  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: iterations,
      hash: "SHA-256",
    },
    derivedKey,
    { name: "AES-CBC", length: keySize },
    true,
    ["encrypt", "decrypt"]
  );

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-CBC", iv: iv },
    key,
    new TextEncoder().encode(plaintext)
  );
  const saltBase64 = arrayBufferToBase64(salt);
  const ivBase64 = arrayBufferToBase64(iv);
  const ciphertextBase64 = arrayBufferToBase64(encrypted);
  const encryptedData = `${ivBase64}::${saltBase64}::${ciphertextBase64}`;

  return btoa(encryptedData);
};

const arrayBufferToBase64 = (buffer: any) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export const whiteSpaceRestrict = (e: any) => {
  const re = /^\S*$/;

  if (!re.test(e.key)) {
    e.preventDefault();
  }
}

export const alphaNumaricInput = (e: any) => {
  const re = /^[ a-zA-Z0-9]+$/;;

  if (!re.test(e.key)) {
    e.preventDefault();
  }
}

export const numaricInput = (e: any) => {
  const re = /^[0-9]+$/;;

  if (!re.test(e.key)) {
    e.preventDefault();
  }
}

export enum FEATURE_NAME {
  CHAT = 'CHAT',
  FREE_CHAT_CREDIT = 'FREE_CHAT',
  FREE_RENTAL_CREDIT = 'FREE_RENTAL',
  FREE_BUY_CREDIT = 'FREE_BUY'
}

export const decryptString = (encryptedData: string) => {
  try {
    const ITERATIONS = 65536;
    const KEY_SIZE = 256 / 32;
    
    const salt = CryptoJS.enc.Utf8.parse(saltKey);
    const key = CryptoJS.PBKDF2(convertKey, salt, {
      keySize: KEY_SIZE,
      iterations: ITERATIONS,
      hasher: CryptoJS.algo.SHA256
    });
    
    const iv = CryptoJS.lib.WordArray.create(new Array(16).fill(0));
    const encrypted = CryptoJS.enc.Base64.parse(encryptedData);
    
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      throw new Error('Decryption failed - empty result');
    }
    
    return decryptedString;
    
  } catch (error: any) {
    console.error('Error decrypting PAYG signup data:', error);
  }
};

export const parsePAYGUserData = (decryptedData: string) => {
  try {
    const encodedData = decryptedData.replace(/\+/g, '%2B');
    const params = new URLSearchParams(encodedData);
    return {
      firstName: params.get('firstName') || '',
      lastName: params.get('lastName') || '',
      email: params.get('email') || '',
      company: params.get('company') || ''
    };
  } catch (error: any) {
    console.error('Error parsing PAYG user data:', error);
  }
};

export const decryptAndParsePAYGData = (encryptedData: string) => {
  const decryptedString = decryptString(encryptedData);
  return parsePAYGUserData(decryptedString);
};